import { Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getAssociationById, updateAssociation } from 'src/shared/services/associationService';
import { getOrganizationAssociationId } from 'src/shared/functions/organizationHelpers';
import useDistrictPickerHeader from 'src/shared/hooks/useDistrictPickerHeader';
import { AssociationRequest } from '../models/associationRequest';
import { AssociationResponse } from '../models/associationResponse';

export const AssocationEditPage = () => {
  const { t } = useTranslation(['association']);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const { currentUser } = useGlobals();
  const params = useParams();
  const currentAssocationId = getOrganizationAssociationId(currentUser, params.id);

  const isMounted = useIsMounted();

  const [, setAssciation] = useState<AssociationResponse>();

  const requiredMessage = t('common:validationMessages.required');

  const AssociationRequestSchema: Schema<AssociationRequest> = yup.object().shape({
    localAss: yup.string().required(requiredMessage),
    nr: yup.string().optional().nullable(),
    shortName: yup.string().optional().nullable(),
    cOaddress: yup.string().optional().nullable(),
    address: yup.string().optional().nullable(),
    addressLocal: yup.string().optional().nullable(),
    postNr: yup.string().optional().nullable(),
    postAddress: yup.string().optional().nullable(),
    homesite: yup.string().optional().nullable(),
    phone: yup.string().optional().nullable(),
    phoneOther: yup.string().optional().nullable(),
    email: yup.string().optional().nullable(),
    emailResponsible: yup.string().optional().nullable(),
    fee: yup.string().optional().nullable(),
    bankGiro: yup.string().optional().nullable(),
    postGiro: yup.string().optional().nullable(),
    orgNr: yup.string().optional().nullable(),
    customerNr: yup.string().optional().nullable(),
    comment: yup.string().optional().nullable(),
  });

  const formMethods = useForm<AssociationRequest>({
    resolver: yupResolver(AssociationRequestSchema),
    mode: 'all',
    defaultValues: {
      localAss: '',
      nr: '',
      shortName: '',
      cOaddress: '',
      address: '',
      addressLocal: '',
      postNr: '',
      postAddress: '',
      homesite: '',
      phone: '',
      phoneOther: '',
      email: '',
      emailResponsible: '',
      fee: '',
      bankGiro: '',
      postGiro: '',
      orgNr: '',
      customerNr: '',
      comment: '',
    },
  });

  const { handleSubmit, trigger, reset } = formMethods;

  const saveFormData = useCallback(
    async (data: AssociationRequest) => {
      setLoading(true);
      try {
        const res = await updateAssociation(data);
        if (isMounted()) {
          ConsoleHelper.log('res updateAssociation', res);
          toast.success(<>{t('gift:createDonor.saveSuccess')}</>);
          navigate(`${RoutesEnum.OrganizationAssocations}`);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, navigate, t],
  );
  const onSubmit = async (data: AssociationRequest) => {
    ConsoleHelper.log('onSubmit', data);
    await trigger();
    await saveFormData(data);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getAssociationById(currentAssocationId);
        if (isMounted()) {
          // ConsoleHelper.log('AssociationById RES:: ', result.data);
          setAssciation(result.data);
          reset(result.data);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };

    fetchData();
  }, [isMounted, currentAssocationId, reset]);

  const { associationName } = useDistrictPickerHeader();

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Grid spacing={3} container>
              <Grid item xs={12} lg={7}>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                  {associationName}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={12} sm={4}>
                    <ControlledTextField label={t('association:nr')} name='nr' />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <ControlledTextField label={t('association:name')} required name='localAss' />
                  </Grid>
                </Grid>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
                  {t('association:other')}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('association:postGiro')} name='postGiro' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('association:bankGiro')} name='bankGiro' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('association:orgNr')} name='orgNr' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('association:comment')} multiline rows={4} name='comment' />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                  {t('association:contact')}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:address')} name='address' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:coAddress')} name='cOaddress' />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField label={t('association:postNr')} name='postNr' />
                  </Grid>
                  <Grid item xs={8}>
                    <ControlledTextField label={t('association:postAddress')} name='postAddress' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('association:addressLocal')} name='addressLocal' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:email')} name='email' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:emailResponsible')} name='emailResponsible' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:phone')} name='phone' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:phoneOther')} name='phoneOther' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('association:homesite')} name='homesite' />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type='submit' variant='contained' disableElevation>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};
